"use client"

/**
 * Site-wide install banner — pinned to the bottom of EVERY public page (PWA-first).
 * Dismissible; reappears after RESHOW_AFTER_MS (so a customer who dismissed once still
 * gets re-offered later). Hidden only when the app is GENUINELY running standalone.
 *
 *  - iPhone/iPad Safari : Apple has no auto-install — show "Tap Share → Add to Home Screen"
 *                         with the visual Share glyph.
 *  - Android Chrome     : beforeinstallprompt → one-tap Install (native dialog).
 *  - Desktop Chrome/Edge: Install button → native dialog.
 *  - macOS Safari/Firefox: short manual hint.
 */
import { useEffect, useState } from "react"
import { X, Share, Download, Plus } from "lucide-react"
import { useInstall } from "./use-install"

const DISMISS_KEY = "spire_install_band_dismissed_at"
const RESHOW_AFTER_MS = 3 * 24 * 60 * 60 * 1000 // re-show 3 days after a dismiss

export function InstallAppBand() {
  const { canInstall, installed, isIos, isMacSafari, isFirefox, manualHint, promptInstall } = useInstall()
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return
    if (installed) { setShow(false); return }
    let dismissedAt = 0
    try { dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || "0") } catch { /* private mode */ }
    const stillDismissed = dismissedAt > 0 && Date.now() - dismissedAt < RESHOW_AFTER_MS
    setShow(!stillDismissed)
  }, [installed])

  if (!show || installed) return null

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())) } catch { /* private mode */ }
    setShow(false)
  }

  // Right-side action by platform.
  let action: React.ReactNode
  if (canInstall) {
    // Android / desktop Chromium — native install dialog.
    action = (
      <button
        onClick={() => void promptInstall()}
        className="flex flex-none items-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-bold text-[#d60500]"
      >
        <Download className="h-4 w-4" /> Install
      </button>
    )
  } else if (isIos) {
    action = (
      <span className="flex flex-none items-center gap-1 rounded-lg bg-white/15 px-3 py-1.5 text-xs font-semibold">
        Tap <Share className="h-4 w-4" /> <span aria-hidden>→</span> Add to Home Screen <Plus className="h-3.5 w-3.5" />
      </span>
    )
  } else if (isMacSafari) {
    action = <span className="flex-none rounded-lg bg-white/15 px-3 py-1.5 text-xs font-semibold">File → Add to Dock</span>
  } else if (isFirefox) {
    action = <span className="flex-none rounded-lg bg-white/15 px-3 py-1.5 text-xs font-semibold">Open in Chrome / Edge</span>
  } else {
    action = (
      <button
        onClick={() => alert(manualHint)}
        className="flex flex-none items-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-bold text-[#d60500]"
      >
        <Download className="h-4 w-4" /> Install
      </button>
    )
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] border-t border-white/15 bg-gradient-to-r from-[#d60500] to-[#b50400] text-white shadow-2xl pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-2.5">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-white p-1">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/icons/icon-192.png" alt="" className="h-full w-full rounded object-contain" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold">Install the Spire App</p>
          <p className="truncate text-xs text-white/85">
            {isIos
              ? "Tap Share → Add to Home Screen for faster access and instant notifications."
              : "Faster access, property alerts, and instant notifications — free, no app store."}
          </p>
        </div>
        {action}
        <button onClick={dismiss} aria-label="Dismiss" className="flex-none rounded-md p-1 text-white/70 hover:text-white">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}
